import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { Modal } from '../common/Modal'
import { Button } from '../common/Button'
import { useToast } from '../common/Toast'

interface VacationPauseModalProps {
  isOpen: boolean
  onClose: () => void
  lovedOneId: Id<'lovedOnes'>
}

export function VacationPauseModal({ isOpen, onClose, lovedOneId }: VacationPauseModalProps) {
  const { t } = useTranslation()
  const { showToast } = useToast()
  const createPause = useMutation(api.vacationPauses.create)

  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date()
    return { year: now.getFullYear(), month: now.getMonth() }
  })
  const [startDate, setStartDate] = useState<string | null>(null)
  const [endDate, setEndDate] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleDateClick = (dateStr: string) => {
    if (!startDate || (startDate && endDate)) {
      // Start new selection
      setStartDate(dateStr)
      setEndDate(null)
      setError('')
    } else {
      // Set end date
      if (dateStr >= startDate) {
        setEndDate(dateStr)
        setError('')
      } else {
        // Clicked before start — restart
        setStartDate(dateStr)
        setEndDate(null)
        setError('')
      }
    }
  }

  const handleSave = async () => {
    if (!startDate || !endDate) {
      setError(t('medicationManagement.vacationPause.invalidDate'))
      return
    }

    setIsSubmitting(true)
    setError('')

    try {
      const result = await createPause({
        lovedOneId,
        startDate,
        endDate,
      })

      if (!result.success) {
        if (result.error === 'An active vacation pause already exists') {
          setError(t('medicationManagement.vacationPause.alreadyPaused'))
        } else if (result.error === 'End date must be on or after start date') {
          setError(t('medicationManagement.vacationPause.endBeforeStart'))
        } else {
          setError(result.error || t('errors.generic'))
        }
        return
      }

      showToast({
        message: t('medicationManagement.toast.pauseCreated'),
        type: 'success',
      })
      onClose()
    } catch {
      setError(t('errors.generic'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const prevMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 0) return { year: prev.year - 1, month: 11 }
      return { year: prev.year, month: prev.month - 1 }
    })
  }

  const nextMonth = () => {
    setCurrentMonth(prev => {
      if (prev.month === 11) return { year: prev.year + 1, month: 0 }
      return { year: prev.year, month: prev.month + 1 }
    })
  }

  const getDaysInMonth = (year: number, month: number): number => {
    return new Date(year, month + 1, 0).getDate()
  }

  const getFirstDayOfWeek = (year: number, month: number): number => {
    return new Date(year, month, 1).getDay()
  }

  const formatDateStr = (year: number, month: number, day: number): string => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
  }

  const isInRange = (dateStr: string): boolean => {
    if (!startDate || !endDate) return false
    return dateStr >= startDate && dateStr <= endDate
  }

  const isSelected = (dateStr: string): boolean => {
    return dateStr === startDate || dateStr === endDate
  }

  const today = new Date()
  const todayStr = formatDateStr(today.getFullYear(), today.getMonth(), today.getDate())

  const monthName = new Date(currentMonth.year, currentMonth.month).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  })

  const daysInMonth = getDaysInMonth(currentMonth.year, currentMonth.month)
  const firstDay = getFirstDayOfWeek(currentMonth.year, currentMonth.month)

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  }

  const navStyles: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  }

  const navButtonStyles: React.CSSProperties = {
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '18px',
    padding: 0,
  }

  const monthLabelStyles: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a1a1a',
  }

  const gridStyles: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '2px',
  }

  const dayHeaderStyles: React.CSSProperties = {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#9ca3af',
    padding: '0.5rem 0',
  }

  const dayCellStyles = (dateStr: string, isPast: boolean): React.CSSProperties => ({
    minWidth: '44px',
    minHeight: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    borderRadius: isSelected(dateStr) ? '8px' : '4px',
    backgroundColor: isSelected(dateStr)
      ? '#2563eb'
      : isInRange(dateStr)
        ? '#dbeafe'
        : 'transparent',
    color: isSelected(dateStr) ? '#fff' : isPast ? '#d1d5db' : '#1a1a1a',
    cursor: isPast ? 'default' : 'pointer',
    fontWeight: isSelected(dateStr) ? 600 : 400,
    border: dateStr === todayStr ? '2px solid #2563eb' : '2px solid transparent',
  })

  const selectionInfoStyles: React.CSSProperties = {
    fontSize: '14px',
    color: '#6b7280',
    textAlign: 'center',
  }

  const errorStyles: React.CSSProperties = {
    padding: '0.75rem 1rem',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    fontSize: '14px',
  }

  const buttonRowStyles: React.CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '0.5rem',
  }

  const dayLabels = [
    t('medicationManagement.vacationPause.dayShort.su'),
    t('medicationManagement.vacationPause.dayShort.mo'),
    t('medicationManagement.vacationPause.dayShort.tu'),
    t('medicationManagement.vacationPause.dayShort.we'),
    t('medicationManagement.vacationPause.dayShort.th'),
    t('medicationManagement.vacationPause.dayShort.fr'),
    t('medicationManagement.vacationPause.dayShort.sa'),
  ]

  const formatDisplayDate = (dateStr: string): string => {
    const [y, m, d] = dateStr.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('medicationManagement.vacationPause.selectDates')}
    >
      <div style={containerStyles}>
        {error && <div style={errorStyles}>{error}</div>}

        <div style={navStyles}>
          <button
            style={navButtonStyles}
            onClick={prevMonth}
            aria-label={t('medicationManagement.vacationPause.previousMonth')}
          >
            &larr;
          </button>
          <span style={monthLabelStyles}>{monthName}</span>
          <button
            style={navButtonStyles}
            onClick={nextMonth}
            aria-label={t('medicationManagement.vacationPause.nextMonth')}
          >
            &rarr;
          </button>
        </div>

        <div style={gridStyles}>
          {dayLabels.map(label => (
            <div key={label} style={dayHeaderStyles}>
              {label}
            </div>
          ))}

          {/* Empty cells for days before first of month */}
          {Array.from({ length: firstDay }, (_, i) => (
            <div key={`empty-${i}`} />
          ))}

          {/* Day cells */}
          {Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1
            const dateStr = formatDateStr(currentMonth.year, currentMonth.month, day)
            const isPast = dateStr < todayStr

            return (
              <button
                key={day}
                style={dayCellStyles(dateStr, isPast)}
                onClick={() => !isPast && handleDateClick(dateStr)}
                disabled={isPast}
                aria-label={`${day}`}
              >
                {day}
              </button>
            )
          })}
        </div>

        <div style={selectionInfoStyles}>
          {startDate && endDate ? (
            <span>
              {formatDisplayDate(startDate)} &mdash; {formatDisplayDate(endDate)}
            </span>
          ) : startDate ? (
            <span>
              {t('medicationManagement.vacationPause.startDate')}: {formatDisplayDate(startDate)}{' '}
              &mdash; {t('medicationManagement.vacationPause.endDate')}: ...
            </span>
          ) : (
            <span>{t('medicationManagement.vacationPause.startDate')}...</span>
          )}
        </div>

        <div style={buttonRowStyles}>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} style={{ flex: 1 }}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSubmitting || !startDate || !endDate}
            style={{ flex: 1 }}
          >
            {isSubmitting ? t('common.saving') : t('medicationManagement.vacationPause.savePause')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
